import React, { useState, useEffect, useMemo, useCallback } from "react";
import FilterView from "../filter";
import Sort from "../sort";
import Content from "../body/content";
import Footer from "../body/footer";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { 
  setEvents, 
  setBodySelectedLeagues, 
  getBodyEventAsyncAction 
} from '../../common/actions/common.actions';
import { useSelector, useDispatch } from 'react-redux';
import { playSong } from "../../common/actions/common.actions";
import { selectLoginData } from "../login/login.actions";
import { IoIosArrowBack } from "react-icons/io";
import { FiFilter, FiSearch } from "react-icons/fi";
import { LiaSortAmountDownSolid } from "react-icons/lia";
import { setConfirmData } from '../../common/actions/common.actions';
import { MdCancel } from "react-icons/md";
import { BiFootball } from 'react-icons/bi';
import { GiSoccerKick } from "react-icons/gi";
import Modal from "../../common/components/modal";

const Body = ({ isLoading, setIsLoading, showToastMessage }) => {
  const navigate = useNavigate();
  const { events, bodySelectedLeagues, confirmData, bodyEvents } = useSelector((state) => state.common);
  const { userDetails } = useSelector(selectLoginData);
  const dispatch = useDispatch();

  const [showFilter, setShowFilter] = useState(false);
  const [leagues, setLeagues] = useState([]);
  const [selectedMatches, setSelectedMatches] = useState([]);
  const [searchInput, setSearchInput] = useState('');
  const [showSorting, setShowSorting] = useState(false);
  const [sort, setSort] = useState(null);
  const [showSearch, setShowSearch] = useState(false);
  const [deselectAll, setDeselectAll] = useState(false);
  const { t, i18n } = useTranslation();
  const [openModal, setOpenModal] = useState(false);
  const [modalTitle, setModalTitle] = useState('');
  const [modalContent, setModalContent] = useState('');
  const [initialLoad, setInitialLoad] = useState(true);

  // Effect 1: Initialize user data and confirm data
  useEffect(() => {
    if (confirmData && confirmData.selectedMatches) {
      setSelectedMatches(confirmData.selectedMatches);
    }
  }, [confirmData]);

  // Effect 2: Process bodyEvents when it changes
  useEffect(() => {
    const processEvents = () => {
      if (!bodyEvents) {
        return;
      }

      // Sort leagues
      if (bodyEvents.leagueList && bodyEvents.leagueList.length > 0) {
        const sortedLeagues = [...bodyEvents.leagueList].sort((a, b) => {
          return a.leagueName.localeCompare(b.leagueName);
        });
        setLeagues(sortedLeagues);
      } else {
        setLeagues([]);
      }

      // Process event data
      const eventData = [];
      if (bodyEvents.eventDetails && bodyEvents.eventDetails.length > 0) {
        bodyEvents.eventDetails.forEach((item) => {
          if (item.eventList && item.eventList.length > 0) {
            item.eventList.forEach((eventItem) => {
              eventData.push({
                ...eventItem,
                leagueName: item.league,
                leagueId: item.leagueId,
              });
            });
          }
        });
      }

      // Sort matches by time
      const sortedMatches = eventData.sort((a, b) => {
        return new Date(a.eventTime) - new Date(b.eventTime);
      });
      
      // Dispatch events to Redux
      dispatch(setEvents(sortedMatches));

      // Initialize selected leagues if empty
      if ((!bodySelectedLeagues || bodySelectedLeagues.length === 0) && !deselectAll && bodyEvents.leagueList && bodyEvents.leagueList.length > 0) {
        const leagueNames = bodyEvents.leagueList.map((league) => league.leagueName);
        dispatch(setBodySelectedLeagues(leagueNames));
      }

      setIsLoading(false);
    };

    if (bodyEvents) {
      processEvents();
    }
  }, [bodyEvents, dispatch, deselectAll, bodySelectedLeagues, setIsLoading]);

  // Effect 3: Filter selected matches when events change
  useEffect(() => {
    if (events && events.length > 0) {
      const validEventIDs = events.map((x) => x.eventId);
      
      // Filter confirm data
      if (confirmData && confirmData.selectedMatches && confirmData.selectedMatches.length > 0) {
        const validConfirmData = confirmData.selectedMatches.filter(x => 
          validEventIDs.includes(x.eventId)
        );
        if (validConfirmData.length !== confirmData.selectedMatches.length) {
          dispatch(setConfirmData({ 
            betAmount: confirmData.betAmount, 
            selectedMatches: validConfirmData 
          }));
        }
      }
      
      // Filter selected matches
      if (selectedMatches && selectedMatches.length > 0) {
        const validSelectedMatches = selectedMatches.filter(x => 
          validEventIDs.includes(x.eventId)
        );
        if (validSelectedMatches.length !== selectedMatches.length) {
          setSelectedMatches(validSelectedMatches);
        }
      }
    }
  }, [events, confirmData, dispatch]); // Removed selectedMatches from dependencies

  // Effect 4: Initial data fetch
  useEffect(() => {
    const fetchData = async () => {
      if (userDetails && initialLoad) {
        setIsLoading(true);
        try {
          await dispatch(getBodyEventAsyncAction());
        } catch (error) {
          console.error("Failed to fetch events:", error);
        } finally {
          setInitialLoad(false);
        }
      }
    };

    fetchData();
  }, [userDetails, dispatch, setIsLoading, initialLoad]);

  // Effect 5: Check for lock status
  useEffect(() => {
    if (bodyEvents?.islock) {
      localStorage.removeItem('token');
      localStorage.removeItem('userDetails');
      navigate("/login");
    }
  }, [bodyEvents, navigate]);

  const handleSelectTeam = useCallback((choosen, match, maxTeam = 1, fromBody = true) => {
    setSelectedMatches(prevSelectedMatches => {
      const ind = prevSelectedMatches && prevSelectedMatches.length > 0
        ? prevSelectedMatches.findIndex((x) => x.eventId === match.eventId)
        : -1;
      
      const isSameItem = prevSelectedMatches && prevSelectedMatches.length > 0
        ? prevSelectedMatches.findIndex((x) => x.eventId === match.eventId && x.choosen === choosen) >= 0
        : false;

      if (!isSameItem && (!prevSelectedMatches || prevSelectedMatches.length < maxTeam)) {
        if (prevSelectedMatches && prevSelectedMatches.length > 0) {
          return [
            ...(ind >= 0
              ? prevSelectedMatches.filter((x) => x.eventId !== match.eventId)
              : prevSelectedMatches),
            { choosen, ...match },
          ];
        } else {
          return [{ choosen, ...match }];
        }
      } else {
        if (fromBody && !isSameItem) {
          return [{ choosen, ...match }];
        } else {
          return prevSelectedMatches.filter((x) => x.eventId !== match.eventId);
        }
      }
    });
  }, []);

  const filteredMatches = useMemo(() => {
    if (!events || events.length === 0) return [];

    // Filter by selected leagues first
    const leagueFiltered = bodySelectedLeagues && bodySelectedLeagues.length > 0
      ? events.filter(event => bodySelectedLeagues.includes(event.leagueName))
      : events;

    // Separate 5% events
    const fivePercentEvents = leagueFiltered.filter(event => event.isFivePercent);
    const otherEvents = leagueFiltered.filter(event => !event.isFivePercent);

    // Sort function
    const sortEvents = (eventsArray) => {
      if (!eventsArray || eventsArray.length === 0) return eventsArray;
      
      const sorted = [...eventsArray];
      
      if (sort === null || sort === "eventTime") {
        return sorted.sort((a, b) => new Date(a.eventTime) - new Date(b.eventTime));
      } else if (sort === "leagueName") {
        return sorted.sort((a, b) => a.leagueName.localeCompare(b.leagueName));
      }
      return eventsArray;
    };

    const sortedFivePercent = sortEvents(fivePercentEvents);
    const sortedOther = sortEvents(otherEvents);
    
    // Combine with 5% events first
    const combinedSortedMatches = [...sortedFivePercent, ...sortedOther];

    // Apply search filter
    if (searchInput.trim()) {
      const searchTerm = searchInput.toLowerCase();
      return combinedSortedMatches.filter((event) => {
        return (
          event.leagueName?.toLowerCase().includes(searchTerm) ||
          event.homeTeam?.toLowerCase().includes(searchTerm) ||
          (event.homeTeamMyan && event.homeTeamMyan.toLowerCase().includes(searchTerm)) ||
          event.awayTeam?.toLowerCase().includes(searchTerm) ||
          (event.awayTeamMyan && event.awayTeamMyan.toLowerCase().includes(searchTerm))
        );
      });
    }

    return combinedSortedMatches;
  }, [events, bodySelectedLeagues, searchInput, sort]);

  const handleFilterClick = useCallback(() => {
    dispatch(playSong());
    setShowFilter(prev => !prev);
  }, [dispatch]);

  const handleFilterApply = useCallback(() => {
    setShowFilter(false);
  }, []);

  const handleLeagueSelection = useCallback((selectedLeagues) => {
    setDeselectAll(selectedLeagues.length === 0);
    dispatch(setBodySelectedLeagues(selectedLeagues));
  }, [dispatch]);

  const handleSortingSelection = React.useCallback((selectedSort) => {
    setSort(selectedSort);
  }, []);

  const handleSortingClick = useCallback(() => {
    dispatch(playSong());
    setShowSorting(prev => !prev);
  }, [dispatch]);

  const handleSortingApply = useCallback(() => {
    setShowSorting(false);
  }, []);

  const handleSearchClick = useCallback(() => {
    dispatch(playSong());
    setShowSearch(prev => !prev);
  }, [dispatch]);

  const handleSearchInputChange = useCallback((event) => {
    setSearchInput(event.target.value);
  }, []);

  const handleSongClick = useCallback(() => {
    dispatch(playSong());
  }, [dispatch]);

  const handleBackClick = useCallback(() => {
    dispatch(playSong());
    navigate('/dashboard');
  }, [dispatch, navigate]);

  const handleClearSearch = useCallback(() => {
    dispatch(playSong());
    setSearchInput('');
    setShowSearch(false);
  }, [dispatch]);

  const isWideWindow = useMemo(() => window.innerWidth >= 1024, []);

  const renderMatches = () => {
    if (!filteredMatches || filteredMatches.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center py-10">
          <BiFootball size={48} className="mb-4 text-blue-primary/50" />
          <p className="text-lg font-medium text-blue-primary/70">
            {t('noMatchesFound') || 'No matches found'}
          </p>
        </div>
      );
    }

    return filteredMatches.map((match) => (
      <li key={match.eventId} className="w-full">
        <Content
          match={match}
          sendDataToParent={handleSelectTeam}
          selectedMatches={selectedMatches}
          eventTime={match.eventTime}
          homeTeam={i18n.language === 'en' ? match.homeTeam : (match.homeTeamMyan || match.homeTeam)}
          awayTeam={i18n.language === 'en' ? match.awayTeam : (match.awayTeamMyan || match.awayTeam)}
          bodyHandicap={match.bodyHandicap}
          goalHandicap={match.goalHandicap}
          isFivePercent={match?.isFivePercent}
          isBodyPage={true}
        />
      </li>
    ));
  };

  return (
    <div className="w-full h-[100dvh] flex flex-col overflow-hidden relative bg-gradient-to-br from-silver via-gray-100 to-silver">
      <div className="relative z-10">
        {/* Header Section */}
        <div className="w-full border-b-2 shadow-2xl bg-gradient-to-r from-white/95 via-white/98 to-white/95 backdrop-blur-xl border-crimson/40">
          <div className="px-2 py-3">
            <div className="flex items-center justify-between">
              {/* Left Section */}
              <div className="flex items-center">
                <button
                  className="flex items-center justify-center w-10 h-10 transition-all duration-300 text-blue-primary hover:bg-crimson/10 rounded-xl"
                  onClick={handleBackClick}
                >
                  <IoIosArrowBack size={24} />
                </button>
                {!showSearch && (
                  <div className="flex items-center space-x-2">
                    <div className="flex items-center justify-center w-12 h-12 border-2 shadow-lg bg-gradient-to-br from-crimson via-red-primary to-crimson rounded-xl border-golden/40">
                      <GiSoccerKick size={22} className="text-white drop-shadow-lg" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold leading-tight text-blue-primary">{t('body')}</h3>
                      <p className="text-xs font-medium text-blue-primary/70">{t('totalGoals')}</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Search Input */}
              {showSearch && (
                <div className="flex-1 mx-4">
                  <div className="relative">
                    <input
                      type="text"
                      className="w-full bg-white border-2 border-crimson/30 rounded-xl px-4 py-2.5 pl-10 pr-10 text-blue-primary placeholder-blue-primary/50 focus:outline-none focus:ring-2 focus:ring-crimson focus:border-crimson transition-all duration-300"
                      placeholder={t('searchMatches') || "Search matches..."}
                      value={searchInput}
                      onChange={handleSearchInputChange}
                    />
                    <FiSearch size={18} className="absolute transform -translate-y-1/2 left-3 top-1/2 text-blue-primary/70" />
                    <button
                      className="absolute transition-colors duration-300 transform -translate-y-1/2 right-3 top-1/2 text-crimson hover:text-blue-primary"
                      onClick={handleClearSearch}
                    >
                      <MdCancel size={20} />
                    </button>
                  </div>
                </div>
              )}

              {/* Right Section */}
              {!showSearch && (
                <div className="flex items-center space-x-2">
                  <button
                    onClick={handleSortingClick}
                    className="flex items-center justify-center w-10 h-10 transition-all duration-300 text-blue-primary hover:bg-crimson/10 rounded-xl"
                    title="Sort"
                  >
                    <LiaSortAmountDownSolid size={20} />
                  </button>
                  <button
                    onClick={handleSearchClick}
                    className="flex items-center justify-center w-10 h-10 transition-all duration-300 text-blue-primary hover:bg-crimson/10 rounded-xl"
                    title="Search"
                  >
                    <FiSearch size={20} />
                  </button>
                  <button
                    onClick={handleFilterClick}
                    className="relative flex items-center justify-center w-10 h-10 transition-all duration-300 text-blue-primary hover:bg-crimson/10 rounded-xl"
                    title="Filter"
                  >
                    <FiFilter size={20} />
                    {bodySelectedLeagues && bodySelectedLeagues.length > 0 && (
                      <div className="absolute w-3 h-3 border-2 border-white rounded-full -top-1 -right-1 bg-crimson"></div>
                    )}
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="absolute bottom-0 left-0 right-0 h-1 shadow-lg bg-gradient-to-r from-crimson via-golden to-crimson"></div>
        </div>
      </div>

      {/* Main Content */}
      <div className="relative z-10 flex-1 overflow-auto bg-transparent">
        <div className="px-1 py-2">
          <ul className="flex flex-col w-full p-0 space-y-1 list-none scroll-smooth">
            {renderMatches()}
          </ul>
        </div>
      </div>

      {/* Footer */}
      <div className="relative z-10 border-t-2 shadow-2xl bg-gradient-to-r from-white/95 via-white/98 to-white/95 backdrop-blur-xl border-crimson/40">
        <div className="absolute top-0 left-0 right-0 h-1 shadow-lg bg-gradient-to-r from-crimson via-golden to-crimson"></div>
        <Footer
          selectedMatches={selectedMatches}
          fromBody={true}
          showToastMessage={showToastMessage}
          onModalTitleChange={setModalTitle}
          onModalContentChange={setModalContent}
          onOpenModalChange={setOpenModal}
        />
      </div>

      {/* Sort Dropdown */}
      {showSorting && (
        <div className="absolute top-20 right-4 bg-gradient-to-br from-white via-white/98 to-white/95 backdrop-blur-xl border-2 border-crimson/30 rounded-xl shadow-2xl p-4 z-40 min-w-[200px]">
          <Sort onClose={handleSortingApply} onSortingSelection={handleSortingSelection} />
        </div>
      )}

      {/* Filter Overlay */}
      <div
        className={`fixed top-0 left-0 w-full ${isWideWindow ? 'w-[85%] left-60' : ''} h-[100dvh] bg-gradient-to-br from-silver via-gray-100 to-silver z-50 transform transition-transform duration-300 ${
          showFilter ? 'translate-y-0' : '-translate-y-full'
        }`}
      >
        <FilterView
          onClose={handleFilterApply}
          leagues={leagues}
          onLeagueSelection={handleLeagueSelection}
          events={events.length}
          mixBody="body"
        />
      </div>
      
      {/* Modal */}
      {openModal && (
        <Modal 
          title={modalTitle} 
          content={modalContent} 
          onClose={() => setOpenModal(false)} 
        />
      )}
    </div>
  );
};

export default React.memo(Body);